import React from 'react';
import './PhotoPage.css';
import {
  PhotoPreset,
  PhotoTextSlot,
  PhotoTextSize,
  PhotoTextColor,
  DEFAULT_DATE_TEXT,
  DEFAULT_DESC_TEXT,
  DEFAULT_TEXT_COLOR,
  photoPresetById,
} from './photoPresets';

export interface PhotoPageProps {
  preset: PhotoPreset;
  photoUrl: string;

  date?: string;
  datePos?: PhotoTextSlot;
  dateSize?: PhotoTextSize;

  description?: string;
  descPos?: PhotoTextSlot;
  descSize?: PhotoTextSize;

  /** Overlay text color (used for fullbleed + center-slot overlays on framed). */
  textColor?: PhotoTextColor;

  footerTitle?: string;
  pageNumber?: number;

  /** Показать красную зону подрезки по периметру страницы — всё, что в неё
   *  попало, типография может срезать. Включаем только на крупном превью
   *  (страница редактирования фото), на миниатюрах это визуальный шум. */
  safeZone?: boolean;

  className?: string;
}

type Slot = 'top' | 'center' | 'bottom';

interface SlotEntry {
  kind: 'date' | 'desc';
  text: string;
  size: PhotoTextSize;
}

export const PhotoPage: React.FC<PhotoPageProps> = ({
  preset,
  photoUrl,
  date = '',
  datePos = DEFAULT_DATE_TEXT.pos,
  dateSize = DEFAULT_DATE_TEXT.size,
  description = '',
  descPos = DEFAULT_DESC_TEXT.pos,
  descSize = DEFAULT_DESC_TEXT.size,
  textColor = DEFAULT_TEXT_COLOR,
  footerTitle,
  pageNumber,
  safeZone = false,
  className = '',
}) => {
  const slots: Record<Slot, SlotEntry[]> = { top: [], center: [], bottom: [] };

  if (date && datePos !== 'hidden') {
    slots[datePos].push({ kind: 'date', text: date, size: dateSize });
  }
  if (description && descPos !== 'hidden') {
    slots[descPos].push({ kind: 'desc', text: description, size: descSize });
  }

  const frame = photoPresetById(preset).frame;
  const isFullbleed = frame === null;

  // Frame container uses inline style — slot rect is centered inside the frame
  // and sized as a percentage of the frame (per asset metadata). Keeping the
  // numbers here means CSS doesn't have to know which preset it's rendering.
  const frameStyle: React.CSSProperties | undefined = frame
    ? {
        width: `${frame.widthRatio * 100}%`,
        aspectRatio: `${frame.frameW} / ${frame.frameH}`,
      }
    : undefined;
  const framePhotoStyle: React.CSSProperties | undefined = frame
    ? {
        width: `${(frame.slotW / frame.frameW) * 100}%`,
        height: `${(frame.slotH / frame.frameH) * 100}%`,
      }
    : undefined;

  return (
    <div className={`php php--${preset} php--color-${textColor} ${className}`}>
      <div className="php__sheet">
        {isFullbleed ? (
          <div className="php__photo-wrap">
            {photoUrl ? (
              <img className="php__photo" src={photoUrl} alt="" />
            ) : (
              <div className="php__photo-empty">фото не выбрано</div>
            )}
          </div>
        ) : (
          <div className="php__frame-wrap" style={frameStyle}>
            <img className="php__frame-bg" src={frame!.src} alt="" />
            {photoUrl ? (
              <img className="php__frame-photo" src={photoUrl} alt="" style={framePhotoStyle} />
            ) : (
              <div className="php__frame-photo php__frame-empty" style={framePhotoStyle}>
                фото не выбрано
              </div>
            )}
          </div>
        )}

        {(['top', 'center', 'bottom'] as Slot[]).map((slot) => {
          const entries = slots[slot];
          if (!entries.length) return null;
          const overlay = isFullbleed || slot === 'center';
          return (
            <div
              key={slot}
              className={`php__slot php__slot--${slot} ${
                overlay ? 'php__slot--overlay' : 'php__slot--paper'
              }`}
            >
              {entries.map((e) => (
                <div
                  key={e.kind}
                  className={`php__text php__text--${e.kind} php__text--${e.size}`}
                >
                  {e.text}
                </div>
              ))}
            </div>
          );
        })}

        <div className="php__footer">
          <span className="php__footer-title">{footerTitle ?? ' '}</span>
          <span className="php__folio">
            {pageNumber !== undefined ? pageNumber : ' '}
          </span>
        </div>

        {/* Зона подрезки — поверх всего, чтобы было видно, какая часть фото
            и подписей может уйти под нож типографии. */}
        {safeZone && <div className="php__bleed" aria-hidden="true" />}
      </div>
    </div>
  );
};
